import { Box, BoxProps, setRef } from '@mui/material'
import { useElementPosition } from '@dtx-company/true-common/src/hooks/useElementPosition'
import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react'

function useClassName(
  width: number | undefined,
  widthClasses: FlexibleBoxProps['widthClasses'],
  className: FlexibleBoxProps['className']
): FlexibleBoxProps['className'] {
  return useMemo(() => {
    if (!width) return className

    const list: string[] = []
    if (widthClasses) {
      Object.keys(widthClasses).forEach(className => {
        const minWidth = widthClasses[className]
        if (width >= minWidth) {
          list.push(className)
        }
      })
    }
    if (className) list.push(className)
    return list.join(' ')
  }, [widthClasses, width, className])
}

interface FlexibleBoxProps extends BoxProps {
  widthClasses?: Record<string, number>
}

/**
 * Creates a flexible box that can adjust its css classes based on its size
 * to facilitate creating css that's responsive to the element's size rather than
 * to the screen's size.
 *
 * @example
 * const _ChildBox = styled(Box)`
 *   width: 100%;
 * `
 * const _RootFlexibleBox = styled(FlexibleBox)`
 *   background-color: red;
 *   &.wide {
 *     background-color: blue;
 *     ${_ChildBox} {
 *       width: 50%;
 *     }
 *   }
 * `
 * <_RootFlexibleBox widthClasses={{ wide: 1024 }}>
 *   <_ChildBox />
 * </_RootFlexibleBox>
 */
export const FlexibleBox = forwardRef(function FlexibleBox(
  props: FlexibleBoxProps,
  ref
): JSX.Element {
  const { widthClasses, ...boxProps } = props
  const boxRef = useRef<HTMLElement | undefined>(undefined)
  const boxPosition = useElementPosition(boxRef.current, { update: 'live' })
  const className = useClassName(boxPosition?.width, widthClasses, boxProps.className)
  const [_, triggerRerender] = useState(0)
  const [shouldRender, setShouldRender] = useState(false)

  const handleRef = useCallback(
    (node: HTMLElement) => {
      setRef(boxRef, node)
      setRef(ref, node)
      // Sometimes like on transition from /signin page to /codes to /paused-account,
      // the ref is getting set but it's not triggering the component to re-render
      // so useElementPosition is not getting the ref and not running so using
      // this common trick to force it
      triggerRerender(num => num + 1)
      // fixes flicker from width calculation
      window.requestAnimationFrame(() => setShouldRender(true))
    },
    [ref]
  )

  const sx = Array.isArray(boxProps.sx) ? boxProps.sx : [boxProps.sx]
  return (
    <Box
      {...boxProps}
      sx={sx.concat({
        ...(shouldRender ? {} : { opacity: 0 })
      })}
      ref={handleRef}
      className={className}
    />
  )
})
